import * as React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    MenuItem,
    Select,
    WithStyles
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography/Typography";
import { styles } from "./OrderDetailsView.styles";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import * as UserDetailsAction from "../../stores/user/UserDetailsAction";
import * as OrderAction from "../../stores/orders/OrderAction";
import { useAuthContext } from "../../auth/AuthProvider";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../constants/ToastStatusEnum";
import { IRemoteDataTableColumn } from "../components/RemoteDataTable/interfaces";
import { Breadcrumbs } from "../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../components/CenteredContainer/CenteredContainer";
import { OrderDetailsComponent } from "./OrderDetailsComponent";
import { defaultValidityDays, processingOrderStats, validityDays, completedOrderStats } from "../../constants/ORDER_DETAILS";
import TableRemoteData, { getParamsFromQuery } from "../components/RemoteDataTable/RemoteDataTable";
import { requestOrderFiles } from "../components/RemoteDataTable/requests";
import { PublicLinksTable } from "../components/PublicLinksTable/PublicLinksTable";
import PermissionGuard from "../components/PermissionGuard/PermissionGuard";
import { isPermissionGranted } from "../../utilities/permissionUtils";
import { useGetRoles } from "../../hooks/useGetRole";
import { AcceptTermsConditionsComponent, IAgrees } from "./AcceptTermsConditionsComponent";
import { BlueButton } from "../components/Buttons/BlueButton";
import * as PublicLinkAction from "../../stores/publicLinks/PublicLinkAction";
import { selectRequesting } from "../../selectors/requesting/RequestingSelector";
import { EmptyOrderFilesComponent } from "../components/EmptyOrderFiles/EmptyOrderFilesComponent";
import { useState } from "react";
import { useHistory } from "react-router-dom";

export interface IOrderDetailsViewProps extends WithStyles<typeof styles> {
}

const OrderDetailsViewPure: React.FunctionComponent<IOrderDetailsViewProps> = ({classes}: IOrderDetailsViewProps) => {

    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {enqueueSnackbar} = useSnackbar();

    const {orderId} = useParams();

    const authContext = useAuthContext();
    var userId = authContext?.user?.id;

    const orders = useSelector((state) => state.orders);
    const requestedOrder = useSelector((state) => state.orders.requestedOrder);
    const roles = useGetRoles();
    const canSeeAllOrders = isPermissionGranted(roles, ["orders.read", "orders.write"]);

    const publicLinksFromState = useSelector(state => state.publicLinks);
    const canSeePublicLinks = isPermissionGranted(roles, ["orders.links.read"]);
    const isRequestingPublicLinks = useSelector(state => selectRequesting(state, [PublicLinkAction.REQUEST_PUBLIC_LINKS_FINISHED]));

    React.useEffect(() => {
        if (canSeeAllOrders) {
            dispatch(OrderAction.requestAllOrders());
        } else {
            dispatch(OrderAction.requestSpecificOrder(userId, orderId));
        }

        if (canSeePublicLinks) {
            dispatch(PublicLinkAction.requestPublicLinks(orderId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //if the current user is admin, we want to get the information about the user who placed the order
    const location = useLocation();
    var showAdminOtherUser = false;
    var showManageUsersBreadcrumbs = false;

    let isAccepted = false;

    var order: any = {};
    if (canSeeAllOrders) {
        if (orders.all && Object.keys(orders.all).length !== 0) {
            if (location.pathname.includes("/admin/manage-orders")) { //the admin is inside the manage orders window
                order = Object.values(orders.all).find(order => order["orderId"] === Number(orderId));
                if (order) {
                    userId = order["userId"];
                    showAdminOtherUser = true;
                    isAccepted = true;
                }
            } else if (location.pathname.includes("/admin/manage-users")) { //the admin is coming from manage users view
                order = Object.values(orders.all).find(order => order["orderId"] === Number(orderId));
                if (order) {
                    userId = order["userId"];
                    showAdminOtherUser = true;
                    showManageUsersBreadcrumbs = true;
                    isAccepted = true;
                }
            } else { //in this case the admin accesses the order details through my-orders
                order = Object.values(orders.all).find(order => order["orderId"] === Number(orderId));
                if (order) {
                    isAccepted = order["termsAccepted"];
                }
            }
        }
    } else {
        order = requestedOrder;
        if (order) {
            isAccepted = order["termsAccepted"];
        }
    }

    const userDetails = useSelector((state) => state.userDetails?.requestedUser);

    React.useEffect(() => {
        dispatch(UserDetailsAction.requestUser(userId));
    }, [dispatch, userId]);

    const userAddress = !userDetails ? null : {
        billingAddress: userDetails.billingAddress,
        deliveryAddress: userDetails.deliveryAddress
    };

    const filescolumns: IRemoteDataTableColumn[] = [
        {
            type: "text",
            title: t("general.filename"),
            field: "filename"
        },
        {
            type: "size",
            title: t("general.size"),
            field: "size"
        },
        {
            title: t("general.download"),
            type: "link",
            sortDisable: true,
            linkField: "link",
            linkTitle: t("general.download")
        }
    ];

    const updateOrder = (order) => {
        const sendDetails = {
            orderState: order.state,
            orderType: order.type,
            adminFee: Number(order.adminFee),
            discount: Number(order.discount),
            vat: Number(order.vat),
            remark: order.remark,
            totalPrice: Number(order.totalPrice)
        };
        dispatch(OrderAction.updateUserOrder(userId, orderId, sendDetails));
        enqueueSnackbar(t("orderDetails.successMessage"), {variant: ToastStatusEnum.Info});
    };

    const handleData = query => {
        return new Promise((resolve, reject) => {
            const params = getParamsFromQuery(query);

            requestOrderFiles(userId, orderId, params)
                .then(response => {
                    resolve({
                        data: response.data.content,
                        page: query.page,
                        totalCount: response.data.totalCount
                    });
                })
                .catch(error => {
                    enqueueSnackbar("List of subscription files cannot be loaded: " + error, {
                        variant: ToastStatusEnum.Error
                    });
                    reject(error);
                });
        });
    };

    const [agrees, setAgrees] = React.useState<IAgrees>({
        agreeTermsCondition: false,
        agreeClientLegal: false
    });

    const handleAgree = (event) => {
        setAgrees({
            ...agrees,
            [event.target.name]: !agrees[event.target.name]
        });
    };

    const isOrderProcessed = processingOrderStats.includes(order?.["state"]);
    const isOrderCompleted = completedOrderStats.includes(order?.["state"]);
    const userFullDetails = userDetails ? `${userDetails.firstName} ${userDetails.lastName} (${userDetails.email})` : "";

    const acceptTermsAndConditions = () => {
        dispatch(OrderAction.acceptTermsConditions(userId, orderId)).then(() => {
            if (canSeeAllOrders) {
                dispatch(OrderAction.requestAllOrders());
            } else {
                dispatch(OrderAction.requestSpecificOrder(userId, orderId));
            }
        });
    };

    const [publicLinkDurationOpen, setPublicLinkDurationOpen] = React.useState(false);
    const handleAddPublicLink = () => {
        let days = {"days": selectedValidityDays};
        dispatch(PublicLinkAction.addPublicLink(orderId, days)).then(() => {
            enqueueSnackbar("Link added", {
                variant: ToastStatusEnum.Info
            });
            dispatch(PublicLinkAction.requestPublicLinks(orderId));
        });
        setPublicLinkDurationOpen(false);
    };
    const handleClosePublicLink = () => {
        setPublicLinkDurationOpen(false);
    };

    const [selectedValidityDays, setSelectedValidityDays] = React.useState(defaultValidityDays);
    const handleValidityDays = (event) => {
        setSelectedValidityDays(event.target.value);
    };

    const [downloadFilesUpdate, setDownloadFilesUpdate] = useState(0);

    const handleEmptyOrderChange = () => {
        setDownloadFilesUpdate(downloadFilesUpdate + 1);
    };

    function isEmptyOrderSelectionAvailable() {
        return order["items"].length === 0 && !["Archived", "Rejected", "Failed"].includes(order.state);
    }

    const isKaiko = (orderItem) => orderItem.filterableProductParameters !== null;

    const [changesUnsaved, setChangesUnsaved] = React.useState(false);
    React.useEffect(() => {
        if (changesUnsaved) {
            const unloadCallback = (event) => {
                event.preventDefault();
                event.returnValue = "";
                return "";
            };
            window.addEventListener("beforeunload", unloadCallback);
            return () => window.removeEventListener("beforeunload", unloadCallback);
        }
    }, [changesUnsaved]);

    const history = useHistory();

    React.useEffect(() => {
        const unblock = history.block((location, action) => {
            if (changesUnsaved) {
                return window.confirm("Changes you made will not be saved. Do you want to leave this page?");
            }
            return true;
        });
        return () => {
            unblock();
        };
    }, [changesUnsaved, history]);

    return order && (Object.keys(order).length !== 0) && userAddress
        ? <CenteredContainer>
            {showAdminOtherUser
                ? <div>
                    {showManageUsersBreadcrumbs
                        ? <Breadcrumbs
                            items={[
                                {title: t("admin.adminPanel"), href: "/admin"},
                                {title: t("admin.manageUsersView.manageUsers"), href: "/admin/manage-users"},
                                {
                                    title: `${userFullDetails} - ${t("general.orders")}`,
                                    href: "/admin/manage-users/" + userId + "/orders"
                                },
                                {
                                    title: t("general.orderNo") + orderId.toString(),
                                    href: "/admin/manage-users/" + userId + "/orders/" + orderId.toString()
                                }
                            ]}
                        />
                        : <Breadcrumbs
                            items={[
                                {title: t("admin.adminPanel"), href: "/admin"},
                                {title: t("admin.manageOrdersView.manageOrders"), href: "/admin/manage-orders"},
                                {
                                    title: t("general.orderNo") + orderId.toString(),
                                    href: "/admin/manage-orders/" + orderId.toString()
                                }
                            ]}
                        />
                    }
                </div>
                : <div>
                    <Breadcrumbs
                        items={[
                            {title: t("manageAccount.myAccount"), href: "/my-account"},
                            {title: t("general.myOrders"), href: "/my-account/my-orders"},
                            {
                                title: t("general.orderNo") + orderId.toString(),
                                href: "/my-account/my-orders/" + orderId.toString()
                            }
                        ]}
                    />
                </div>
            }
            {!isAccepted
                ? <div>
                    <Typography variant="h2" className={classes.orderTitle}>Accept terms and conditions first</Typography>
                    <AcceptTermsConditionsComponent agrees={agrees} handleAgree={handleAgree} />
                    <BlueButton onClick={acceptTermsAndConditions}
                                disabled={((agrees.agreeTermsCondition && agrees.agreeClientLegal) === false)}>Accept</BlueButton>
                </div>
                : <div>
                    <Typography variant="h1"
                                className={classes.orderTitle}>{t("general.orderNo") + orderId.toString()}</Typography>
                    <OrderDetailsComponent order={order} orderId={orderId} userId={userId} updateOrder={updateOrder} showAdminOtherUser={showAdminOtherUser} 
                    detailsChanged={changesUnsaved} setDetailsChanged={setChangesUnsaved} />

                    {showAdminOtherUser && isEmptyOrderSelectionAvailable()
                        ? <>
                            <Typography variant="h2" className={classes.sectionTitle}>Add files to empty order</Typography>
                            <Typography variant="body1">The selection will immediately be saved and appended to the order.</Typography>
                            <EmptyOrderFilesComponent orderId={orderId} onPostChange={handleEmptyOrderChange} />
                        </>
                        : null}

                    {order.items.length !== 0 && order.items.every(isKaiko)
                        ? <Typography variant="h3">Please use the customer API to download the files.</Typography>
                        : <>
                            <Typography variant="h2" className={classes.sectionTitle}>Download</Typography>
                            {order.items.some(isKaiko)
                                ? <Typography variant="h4">The KAIKO products are only available to download via the Customer API.</Typography>
                                : null
                            }
                            <PermissionGuard scope="orders.links.read">
                                <div>
                                    <Typography variant="h3" className={classes.sectionTitle}>Public download links</Typography>
                                    <PublicLinksTable orderId={orderId} onAddPublicLink={setPublicLinkDurationOpen}
                                                    publicLinksFromState={publicLinksFromState}
                                                    isRequesting={isRequestingPublicLinks} />
                                </div>
                            </PermissionGuard>
                            <div>
                                <Typography variant="h3" className={classes.sectionTitle}>{t("orderDetails.downloadFiles")}</Typography>
                                {!showAdminOtherUser && order.items.length === 0 && !isOrderCompleted
                                    ? <Typography variant="body1">The files will be available for download only if the order is in the COMPLETED status.</Typography>
                                    : <TableRemoteData columns={filescolumns} onQueryChange={handleData} update={downloadFilesUpdate} emptyDataMessage={isOrderProcessed ? "Please wait. The order is being processed." : null} />
                                }
                            </div>
                        </>
                    }
                </div>
            }
            <Dialog open={publicLinkDurationOpen} onClose={handleClosePublicLink}>
                <DialogTitle>Add a public link</DialogTitle>
                <DialogContent>
                    <Typography>Select the duration of the public link's validity:</Typography>
                    <Grid container direction="row" alignItems="center">
                        <Grid item xs={1} sm={3}>
                            <FormControl required variant="outlined">
                                <Select
                                    labelId="selectedValidityDays"
                                    id="selectedValidityDays"
                                    name="selectedValidityDays"
                                    value={selectedValidityDays}
                                    onChange={handleValidityDays}
                                >
                                    {validityDays.map(os => (
                                        <MenuItem key={os} value={os}>{os}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={1} sm={3}>
                            <Typography>days.</Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <BlueButton onClick={handleAddPublicLink}>Create link</BlueButton>
                </DialogActions>
            </Dialog>
        </CenteredContainer>
        : null;
};

export const OrderDetailsView = withStyles(styles)(OrderDetailsViewPure);
